/**
 * Sprint 3.1 — Single Source of Truth per lo stato Premium di uno chef.
 *
 * Modulo PURO e ISOLATO. Non importato da nessun file consumer.
 * Non modifica comportamento applicativo. Sostituirà gradualmente le 8
 * implementazioni inline di `premium_until > now()` sparse nel codice
 * (cfr. documento Sprint 3).
 *
 * API stabile esposta:
 *   - type PremiumStatus
 *   - derivePremiumStatus(input, now?)
 *
 * Regole:
 *   - isPremium  = premium_until valido e futuro
 *   - isFounder  = founder_badge === true (indipendente da premium_until)
 *   - source     = 'stripe' se esiste subscription Stripe attiva/trialing
 *                  con current_period_end futuro;
 *                  'launch' se premium attivo deriva solo da grant founder;
 *                  'none' altrimenti
 *   - daysRemaining = ceil(ms(premium_until - now) / 1 giorno), 0 se scaduto
 */

export type PremiumSource = "stripe" | "launch" | "none";

export type PremiumStatus = {
  isPremium: boolean;
  isFounder: boolean;
  premiumUntil: Date | null;
  source: PremiumSource;
  daysRemaining: number;
};

export type PremiumStatusInput = {
  /** chefs.premium_until (ISO string o Date) */
  premium_until?: string | Date | null;
  /** chefs.founder_badge */
  founder_badge?: boolean | null;
  /**
   * Riga `subscriptions` più recente per l'utente (opzionale).
   * Se assente, la sorgente viene inferita dalla sola colonna chefs.
   */
  subscription?: {
    source?: string | null; // 'stripe' | 'launch' | null
    status?: string | null; // 'active' | 'trialing' | 'canceled' | ...
    current_period_end?: string | Date | null;
  } | null;
};

const MS_PER_DAY = 86_400_000;

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Funzione pura. `now` iniettabile per test deterministici.
 */
export function derivePremiumStatus(
  input: PremiumStatusInput | null | undefined,
  now: Date = new Date(),
): PremiumStatus {
  const safe = input ?? {};
  const premiumUntil = toDate(safe.premium_until);
  const isPremium = !!premiumUntil && premiumUntil.getTime() > now.getTime();
  const isFounder = safe.founder_badge === true;

  let source: PremiumSource = "none";
  if (isPremium) {
    const sub = safe.subscription ?? null;
    const subEnd = toDate(sub?.current_period_end);
    const stripeActive =
      sub?.source === "stripe" &&
      (sub?.status === "active" || sub?.status === "trialing") &&
      (!subEnd || subEnd.getTime() > now.getTime());
    if (stripeActive) source = "stripe";
    else source = "launch";
  }

  const daysRemaining = isPremium && premiumUntil
    ? Math.max(0, Math.ceil((premiumUntil.getTime() - now.getTime()) / MS_PER_DAY))
    : 0;

  return { isPremium, isFounder, premiumUntil, source, daysRemaining };
}